import { useState } from 'react';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Filters,
  NotProvided,
  Page,
  PageHeader,
  Pagination,
  Table,
  type Action,
  type FilterFieldSlot,
} from '@evogard/ui';
import {
  COMPANY_TYPES_BY_CATEGORY,
  count,
  formatDocument,
  formatPhone,
  type CompanyCategory,
  type CompanyType,
} from '@evogard/domain';
import { useSessionContext } from '../../../lib/session.js';
import { useDebouncedTerm } from '../../../lib/debounce.js';
import { DataCell } from '../DataCell.js';
import {
  ASSISTANCE_PAYMENT_SHORT,
  CompanyAssistance,
  useAssistancePermissions,
} from './CompanyAssistance.js';
import { CompanyForm } from './CompanyForm.js';
import {
  CATEGORY_COPY,
  COMPANY_TYPE_LABELS,
  useCompanyMutations,
  useCompanyPermissions,
  useCompanySearch,
  type Company,
} from './useCompanies.js';

/** Busca começa em 2 letras — antes disso, casaria quase todo mundo. */
const MIN_SEARCH = 2;

/**
 * Sinistros › Cadastros › Prestadores e › Instituições — a mesma tabela
 * (`companies`), em duas telas (decidido em 25/09).
 *
 * **Prestadores** são quem presta serviço ao sinistro: oficina, prestador de
 * serviços e fornecedor de peças. **Instituições** são quem só recebe
 * pagamento, na Indenização: instituição financeira e órgão público. Quem
 * cadastra uma oficina não precisa ver o Detran na lista, e vice-versa — mas
 * os dois são dono de beneficiário e de despesa pelo mesmo caminho, e por isso
 * o banco não os separa.
 *
 * O esquema da tabela é o de Terceiros (e o de Comercial › Vendas): a busca na
 * barra vale ao digitar; o painel de filtros só vale quando alguém confirma;
 * as ações ficam nas reticências; a paginação é do servidor.
 */
export function CompaniesScreen({ category }: { category: CompanyCategory }) {
  const { data: context } = useSessionContext();
  const { canCreate, canEdit } = useCompanyPermissions();
  const assistance = useAssistancePermissions();
  const { archive } = useCompanyMutations();
  const copy = CATEGORY_COPY[category];
  const institution = category === 'instituicoes';

  const [search, setSearch] = useState('');
  const term = useDebouncedTerm(search);
  const [type, setType] = useState<CompanyType | ''>('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [creating, setCreating] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const effective = term.trim().length >= MIN_SEARCH ? term : '';
  const query = useCompanySearch(category, {
    search: effective,
    type: type || undefined,
    includeArchived,
    page,
    perPage,
  });
  const result = query.data;
  const hasFilter = effective !== '' || type !== '' || includeArchived;

  const fields: FilterFieldSlot[] = [
    {
      type: 'selecao',
      name: 'tipo',
      label: 'Tipo',
      placeholder: 'Todos',
      options: COMPANY_TYPES_BY_CATEGORY[category].map((t) => ({
        value: t,
        label: COMPANY_TYPE_LABELS[t],
      })),
    },
    {
      type: 'selecao',
      name: 'situacao',
      label: 'Situação',
      placeholder: 'Só os ativos',
      help: 'Arquivado não aparece para escolher num sinistro nem num beneficiário novo.',
      options: [{ value: 'todos', label: 'Ativos e arquivados' }],
    },
  ];

  return (
    <Page measure="cheia">
      <PageHeader
        title={copy.title}
        actions={
          /* Esconder é cortesia; a rota continua exigindo `claims.companies:criar`. */
          canCreate &&
          !creating && (
            <Button icon="mais" onClick={() => setCreating(true)}>
              {copy.newLabel}
            </Button>
          )
        }
      >
        {copy.description} Cadastro de {context?.currentTenant.nome}.
      </PageHeader>

      {creating && canCreate && (
        <Card
          title={copy.newLabel}
          actions={
            <Button variant="discreto" size="sm" onClick={() => setCreating(false)}>
              Fechar
            </Button>
          }
        >
          <CompanyForm
            category={category}
            onSaved={() => setCreating(false)}
            onCancel={() => setCreating(false)}
          />
        </Card>
      )}

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
      {actionError && <Alert tone="erro">{actionError}</Alert>}

      {/* Erro sem dado: o aviso acima é a resposta inteira — a tabela diria
          "nenhum" logo abaixo, e é a afirmação que parece fato. */}
      {(!query.isError || result) && (
        <Card
          title={
            result === undefined
              ? 'Carregando…'
              : count(result.total, copy.singular, copy.plural)
          }
          descricao={includeArchived ? 'Ativos e arquivados.' : 'Só os ativos.'}
          bleed
        >
          <Table
            paginate={false}
            search={{
              label: `Pesquisar ${copy.plural}`,
              placeholder: institution ? 'Nome ou CNPJ' : 'Nome, CPF ou CNPJ',
              value: search,
              onChange: (value) => {
                setSearch(value);
                setPage(1);
              },
            }}
            toolbar={
              <Filters
                values={{ tipo: type, situacao: includeArchived ? 'todos' : '' }}
                fields={fields}
                onApply={(values) => {
                  setType(String(values.tipo ?? '') as CompanyType | '');
                  setIncludeArchived(values.situacao === 'todos');
                  setPage(1);
                }}
              />
            }
            label={`${copy.title} ${copy.registered} nesta base`}
            items={result?.items ?? []}
            loading={query.isLoading}
            skeletonRows={perPage}
            rowKey={(c) => String(c.id)}
            rowTitle={(c) => c.name}
            rowSubtitle={(c) => COMPANY_TYPE_LABELS[c.type as CompanyType]}
            rowIcon={copy.icon}
            rowActions={(c): Action[] => {
              const archived = Boolean(c.archivedAt);
              /*
               * A Assistência 24h da empresa (30/10/2026) — o prestador da
               * assistência é esta mesma empresa. Vale com as permissões da
               * assistência, e não com as do cadastro.
               */
              const assistanceAction: Action[] =
                assistance.canView &&
                (c.type === 'prestador_servicos' || c.assistancePaymentMode !== null)
                  ? [
                      {
                        label: 'Assistência 24h',
                        icon: 'assistencia',
                        disabled:
                          archived && c.assistancePaymentMode === null
                            ? 'O cadastro está arquivado. Reative antes de incluir na assistência.'
                            : (false as const),
                        /* Larga: a tabela de serviços põe forma e os três valores numa linha. */
                        formSize: 'xl',
                        form: (close) => <CompanyAssistance company={c} onDone={close} />,
                      },
                    ]
                  : [];
              if (!canEdit) return assistanceAction;
              return [
                ...assistanceAction,
                {
                  label: 'Corrigir',
                  icon: 'etiqueta',
                  disabled: archived
                    ? 'O cadastro está arquivado. Reative antes de corrigir.'
                    : (false as const),
                  form: (close) => (
                    <CompanyForm
                      category={category}
                      editing={c}
                      onSaved={close}
                      onCancel={close}
                    />
                  ),
                },
                {
                  label: archived ? 'Reativar' : 'Arquivar',
                  icon: archived ? 'atualizar' : 'bloqueado',
                  run: () => {
                    setActionError(null);
                    archive.mutate(
                      { id: c.id, archived: !archived },
                      { onError: (e: Error) => setActionError(e.message) },
                    );
                  },
                },
              ];
            }}
            empty={
              /* Sem "ainda": a lista esconde o arquivado, e pode haver um. */
              hasFilter
                ? `${copy.none} com esses filtros. Isso é sobre o recorte, não sobre a base.`
                : `${copy.noneActive} nesta base. O que foi arquivado aparece pelo filtro Situação.`
            }
            columns={[
              {
                title: 'Nome',
                alignment: 'inicio' as const,
                minWidth: '15rem',
                cell: (c) => (
                  <div className="flex min-w-0 items-center gap-2.5">
                    <Avatar nome={c.name} size="md" />
                    <p className="truncate font-semibold">{c.name}</p>
                    {c.archivedAt && <Badge tone="neutro">Arquivado</Badge>}
                    {c.assistancePaymentMode !== null && assistance.canView && (
                      <Badge tone={c.assistanceArchivedAt ? 'neutro' : 'informacao'}>
                        {c.assistanceArchivedAt
                          ? 'Fora da Assistência 24h'
                          : `Assistência 24h · ${ASSISTANCE_PAYMENT_SHORT[c.assistancePaymentMode]}`}
                      </Badge>
                    )}
                  </div>
                ),
              },
              {
                title: 'Tipo',
                minWidth: '11rem',
                cell: (c) => (
                  <Badge tone="informacao">
                    {COMPANY_TYPE_LABELS[c.type as CompanyType]}
                  </Badge>
                ),
              },
              {
                title: institution ? 'CNPJ' : 'CPF/CNPJ',
                minWidth: '12rem',
                cell: (c) => (
                  <DataCell icon="documento">
                    <span className="tabular-nums">{formatDocument(c.document)}</span>
                  </DataCell>
                ),
              },
              ...(institution
                ? []
                : [
                    {
                      title: 'Telefone',
                      minWidth: '11rem',
                      cell: (c: Company) => (
                        <DataCell icon="telefone">
                          {c.phone ? (
                            <span className="tabular-nums">{formatPhone(c.phone)}</span>
                          ) : (
                            <NotProvided />
                          )}
                        </DataCell>
                      ),
                    },
                    {
                      title: 'E-mail',
                      minWidth: '13rem',
                      cell: (c: Company) => (
                        <DataCell icon="email" hint={c.email ?? undefined}>
                          {c.email ? (
                            <span className="truncate">{c.email}</span>
                          ) : (
                            <NotProvided />
                          )}
                        </DataCell>
                      ),
                    },
                  ]),
            ]}
          />
          {result && (
            <Pagination
              page={result.page}
              perPage={result.perPage}
              total={result.total}
              onChange={setPage}
              onPerPageChange={(n) => {
                setPerPage(n);
                setPage(1);
              }}
              label={copy.plural}
              className="border-t border-borda-suave px-5 py-4"
            />
          )}
        </Card>
      )}
    </Page>
  );
}
